import { Component, OnInit } from '@angular/core';
import { Location, NgFor, NgIf } from '@angular/common';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { MainButtonComponent } from '../../shared/ui/main-button/main-button.component';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSelectModule } from 'ng-zorro-antd/select';
import {
  FormBuilder,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { FormTextareaComponent } from '../../shared/ui/form-textarea/form-textarea.component';
import { FormSelectComponent } from '../../shared/ui/form-select/form-select.component';
import { FormInputComponent } from '../../shared/ui/form-input/form-input.component';
import { GetMatchcodesOutput } from '../../api/provision-request-api.types';
import { ClothingApiService } from '../../api/clothing-api.service';
import { DateTime } from 'luxon';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CreateClothingInput,
  GetClothingOutput,
} from '../../api/clothing-api.types';
import { Firma } from '../../api/person-api.types';

@Component({
  selector: 'app-clothing-detail',
  standalone: true,
  imports: [
    NgFor,
    NgIf,
    FormsModule,
    MainButtonComponent,
    NzIconModule,
    NzAutocompleteModule,
    NzDatePickerModule,
    NzSpinModule,
    NzSelectModule,
    NzModalModule,
    NzTimelineModule,
    ReactiveFormsModule,
    NzCollapseModule,
    FormTextareaComponent,
    FormSelectComponent,
    FormInputComponent,
  ],
  templateUrl: './new-clothing-detail.component.html',
  styleUrl: './new-clothing-detail.component.scss',
})
export class NewClothingDetailComponent implements OnInit {
  isSaving = false;
  duplicateModalVisible = false;
  duplicateBarcode = '';
  duplicateClothing: GetClothingOutput | null = null;
  duplicateServerMessage = '';
  duplicateReason: 'existing' | 'blocked' | 'unknown' = 'existing';
  date: Date[] = [];
  companies: Firma[] = [];
  selectSizes: GetMatchcodesOutput[] = [];
  selectClothingType: GetMatchcodesOutput[] = [];
  selectGender = [
    { valueKey: 1, labelKey: 'Männlich' },
    { valueKey: 2, labelKey: 'Weiblich' },
    { valueKey: 3, labelKey: 'Divers' },
  ];

  validateClothingForm = this.fb.group({
    optionalFields: this.fb.group({
      bemerkung: this.fb.control<string | null>(null),
    }),
    mandatoryFields: this.nonNullableFb.group({
      firmaId: this.fb.control<number | null>(null),
      kleidergroesse: this.nonNullableFb.control(0, [Validators.required]),
      kleidungsart: this.nonNullableFb.control(0, [Validators.required]),
      geschlecht: this.nonNullableFb.control(0, [Validators.required]),
      inventarnummer: this.nonNullableFb.control('', [Validators.required]),
    }),
  });

  constructor(
    private fb: FormBuilder,
    private nonNullableFb: NonNullableFormBuilder,
    private clothingApiService: ClothingApiService,
    private location: Location,
    private router: Router,
    private route: ActivatedRoute,
    private message: NzMessageService,
  ) {
    this.companies = this.route.snapshot.data['companies'] ?? [];
  }

  async ngOnInit() {
    this.selectSizes = await this.clothingApiService.getClothingSizes();
    this.selectClothingType = await this.clothingApiService.getClothingType();
  }

  saveAndNavigateBack() {
    this.location.back();
  }

  navigateToNewClothing(newId: number) {
    this.router.navigate(['clothing', newId]);
  }

  async saveClothing() {
    if (this.isSaving) {
      return;
    }

    if (this.validateClothingForm.invalid) {
      this.validateClothingForm.markAllAsTouched();
      this.message.warning('Bitte alle Pflichtfelder ausfüllen.');
      return;
    }

    const mandatoryValues = this.validateClothingForm
      .get('mandatoryFields')
      ?.getRawValue();

    const optionalValues = this.validateClothingForm
      .get('optionalFields')
      ?.getRawValue();

    const newClothing: CreateClothingInput = {
      ...mandatoryValues,
      ...optionalValues,
      firmaId: mandatoryValues?.firmaId ?? undefined,
    };

    this.isSaving = true;

    try {
      const result =
        await this.clothingApiService.createNewClothing(newClothing);

      if (result.status === 'created') {
        this.message.success('Kleidungsstück erfolgreich angelegt.');
        this.navigateToNewClothing(result.clothing.id);
        return;
      }

      if (result.status === 'conflict') {
        await this.handleDuplicateBarcode(
          newClothing.inventarnummer,
          result.message,
        );
      }
    } finally {
      this.isSaving = false;
    }
  }

  private async handleDuplicateBarcode(
    inventarnummer: string,
    serverMessage: string,
  ) {
    const matches =
      await this.clothingApiService.findClothingByBarcode(inventarnummer);
    const existing =
      matches?.find((clothing) => clothing.inventarnummer === inventarnummer) ??
      null;

    this.duplicateBarcode = inventarnummer;
    this.duplicateClothing = existing;
    this.duplicateServerMessage = serverMessage;
    this.duplicateReason = existing
      ? 'existing'
      : matches === null
        ? 'unknown'
        : 'blocked';
    this.duplicateModalVisible = true;

    const barcodeControl = this.validateClothingForm.get(
      'mandatoryFields.inventarnummer',
    );
    barcodeControl?.setValue('');
    barcodeControl?.markAsTouched();
  }

  closeDuplicateModal() {
    this.duplicateModalVisible = false;
  }

  openDuplicateClothing() {
    if (this.duplicateClothing) {
      this.duplicateModalVisible = false;
      this.navigateToNewClothing(this.duplicateClothing.id);
    }
  }

  get duplicateTitle() {
    return this.duplicateReason === 'blocked'
      ? 'Barcode gesperrt'
      : 'Barcode bereits vergeben';
  }

  get duplicateDescription() {
    switch (this.duplicateReason) {
      case 'existing':
        return 'Dieser Barcode gehört bereits zu einem angelegten Kleidungsstück. Die übrigen Eingaben bleiben erhalten.';
      case 'blocked':
        return 'Dieser Barcode wurde bereits verwendet und ist dauerhaft gesperrt, weil das zugehörige Kleidungsstück gelöscht wurde. Bitte vergeben Sie einen anderen Barcode.';
      default:
        return `${this.duplicateServerMessage} Das vorhandene Kleidungsstück konnte nicht geladen werden. Bitte vergeben Sie einen anderen Barcode.`;
    }
  }

  get duplicateDetails(): { label: string; value: string }[] {
    const clothing = this.duplicateClothing;

    if (!clothing) {
      return [];
    }

    const details = [
      { label: 'Bezeichnung', value: clothing.bezeichnung },
      { label: 'Größe', value: clothing.kleidergroesse_text },
      { label: 'Status', value: clothing.status_text },
      { label: 'Firma', value: clothing.firma },
      {
        label: 'Zugewiesen an',
        value: clothing.person
          ? `${clothing.person.vorname} ${clothing.person.name}`
          : undefined,
      },
    ];

    return details.filter(
      (detail): detail is { label: string; value: string } => !!detail.value,
    );
  }

  formatDate(date: string) {
    return DateTime.fromISO(date).toFormat('D', { locale: 'de' });
  }
}
